import { SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import moment, { Moment } from "moment-timezone";
import "./StarlinkCharts.css";
import { BINS, getAllowedBins, getAnalyticsSource, getDecodeURI, getEncodedURI, getMinutes, getTimezoneCity } from '../../utils/util';
import { CsvParameters, downloadAsCSV } from "../../actions/Users/authenticateCsvDownload";
import DownloadCSV, { clearCsvDownload, escapeJsonString } from "../DownloadCSV";
import { clearMetricSeries, getDeployedUserTerminalsByDP, getStarlinkMetricSeries, paramGetStarlinkUserTerminalAlerts, getStarlinkUserTerminalAlerts, paramClearMetricSeries, paramGetStarlinkMetricSeries, clearComponentData } from "./slice";
import { Autocomplete, Button, Dialog, Typography, TableBody, TableCell, TableHead, TableRow, InputAdornment, Checkbox, AutocompleteChangeDetails, AutocompleteChangeReason, Box, Chip, FormControl, FormControlLabel, Grid, IconButton, MenuItem, Select, Switch, Table, TableContainer, TextField, CircularProgress } from "@mui/material";
import { UserTerminal } from "../StarlinkAdmin/types";
import { AVAILABLE_STARLINK_METRICS, STARLINK_METRIC_DISPLAY_NAMES, YYYY_MM_DD_HH_MM_SS, StarlinkMetric, STARLINK_METRIC_UNITS, Interval } from "./constants";
import Chart from "../Charts";
import "../SiteCharts/SiteCharts.css";
import _, { cloneDeep } from "lodash";
import { CHART_COLORS } from "../../utils/Colors";
import { alertMap } from "../../utils/constants";
import Highcharts, { color } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Download from '../../asset/image/DownloadUsage.svg';
import Exporting from 'highcharts/modules/exporting';
import ExportData from 'highcharts/modules/export-data';

interface StarlinkAnalyticsProps {
    authReducer: any;
    errorReducer: any;
    history: any;
    location: any;
    dpIds: string[];
    userTerminals: any[];
    gettingUserTerminals: boolean;
    series: any;
    gettingMetricSeries: number;
    newSummaryStartDate: Moment;
    newSummaryEndDate: Moment;
    getDeployedUserTerminalsByDP: (parameters: any) => void;
    getStarlinkMetricSeries: (parameters: paramGetStarlinkMetricSeries) => void;
    getStarlinkUserTerminalAlerts: (parameters: paramGetStarlinkUserTerminalAlerts) => void;
    userTerminalAlerts: any;
    userTerminalAlertColumns: any;
    clearMetricSeries: (parameters: paramClearMetricSeries) => void;
    downloadAsCSV: (parameters: any) => void;
    clearComponentData: () => void;
    newSummaryServiceLine: any;
}

const getInterval = (minutes: number): string => {
    if (minutes <= 60) return "1 minute";
    if (minutes <= 1440) return "3 minute";
    if (minutes <= 10080) return "20 minute";
    return "12 hour";
};

const StarlinkAnalytics = (props: StarlinkAnalyticsProps) => {
    const {
        authReducer,
        location,
        dpIds,
        userTerminals,
        gettingUserTerminals,
        userTerminalAlerts,
        series,
        newSummaryStartDate,
        newSummaryEndDate,
        newSummaryServiceLine,
        getDeployedUserTerminalsByDP,
        getStarlinkMetricSeries,
        getStarlinkUserTerminalAlerts,
        clearMetricSeries,
        clearComponentData
    } = props;
    const queryParameters = new Map<string, string>(Object.entries(getDecodeURI(location.search)));
    const [selectedUserTerminals, setSelectedUserTerminals] = useState<UserTerminal[]>([]);
    const [selectedMetrics, setSelectedMetrics] = useState<StarlinkMetric[]>([AVAILABLE_STARLINK_METRICS[0]]);
    const getSafeMoment = (value: Moment | undefined | null, fallback: Moment) => {
        if (moment.isMoment(value) && value.isValid()) return value;
        return fallback;
    };
    const [terminalChange, setTerminalChange] = useState(false)
    const initialEnd = getSafeMoment(newSummaryEndDate, moment());
    const initialStart = getSafeMoment(
        newSummaryStartDate,
        moment(initialEnd).clone().subtract(30, 'days')
    );

    const [chartParams, setChartParams] = useState({
        startDate: initialStart,
        endDate: initialEnd,
        selectedBin: getInterval(getMinutes(initialStart, initialEnd))
    });

    const HighchartsInstance = { ...Highcharts };
    Exporting(HighchartsInstance);
    ExportData(HighchartsInstance);
    Highcharts.setOptions({ exporting: { enabled: false } });

    const defaultChartOptions = {
        time: { timezone: getTimezoneCity(authReducer?.userTimezone) },
        chart: {
            type: 'spline',
            zoomType: 'x',
            height: "400px",
            style: { fontFamily: "Roboto, Nunito Sans, Arial, Verdana, Helvetica, sans-serif" },
            plotBorderColor: '#e8eaeb',
            plotBorderWidth: 1
        },
        legend: {
            layout: 'horizontal',
            align: 'right',
            verticalAlign: 'top',
            y: 12,
            margin: 28,
            itemStyle: { color: '#3F3F3F' }
        },
        title: { align: 'left', floating: true, x: 12, y: 32, style: { fontWeight: '500' } },
        plotOptions: {
            series: {
                turboThreshold: 1000000,
                stickyTracking: false,
                connectNulls: false,
            }
        },
        credits: { enabled: false },
        xAxis: {
            type: 'datetime',
            gridLineWidth: 0.5,
        },
        yAxis: { title: { text: undefined } },
        lang: { noData: "No Data" },
        noData: {
            style: {
                fontWeight: 'bold',
                fontSize: '15px',
                color: '#303030',
            },
        },
        series: {
            type: 'spline',
            marker: { symbol: 'circle' },
        },
        exporting: {
            enabled: false,
            csv: {
                columnHeaderFormatter: (item) => {
                    if (item.isXAxis) return 'Time';
                    if (item.name.includes('Throughput')) return `${item.name} (Mbps)`;
                    if (item.name.includes('Time')) return `${item.name} (%)`;
                    if (item.name.includes('Drop Rate')) return `${item.name} (%)`;
                    if (item.name.includes('Latency')) return `${item.name} (ms)`;
                    if (item.name.includes('Signal')) return `${item.name} (%)`;
                    return item.name;
                }
            }
        },
        showLegendTable: true
    };

    const [chartOptions, setChartOptions] = useState<any>(defaultChartOptions);
    const chartRef = useRef<HighchartsReact.RefObject>(null);
    let globalColorCount = 0;
    const previousLine = useRef<any>(null);

useEffect(() => {
    const hasServiceLines = Array.isArray(newSummaryServiceLine) && newSummaryServiceLine.length > 0;
    const hasDpIds = Array.isArray(dpIds) && dpIds.length > 0;

    if (!hasServiceLines || !hasDpIds) return;

    const filteredLines = newSummaryServiceLine.filter(sl => sl !== '');

    const currentInputs = { dpIds, serviceLines: filteredLines };
    if (_.isEqual(previousLine.current, currentInputs)) return;
    previousLine.current = currentInputs;

    setSelectedUserTerminals([]);

    getDeployedUserTerminalsByDP({
        dpIds,
        serviceLines: filteredLines,
    });

    AVAILABLE_STARLINK_METRICS.forEach(metric => {
        clearMetricSeries({
            metric,
            userTerminalIds: undefined
        });
    });

    clearComponentData();
}, [newSummaryServiceLine, dpIds]);


useEffect(() => {
    if (
        !moment.isMoment(newSummaryStartDate) ||
        !moment.isMoment(newSummaryEndDate) ||
        !newSummaryStartDate.isValid() ||
        !newSummaryEndDate.isValid()
    ) return;

    if (!newSummaryStartDate.isSameOrBefore(newSummaryEndDate)) return;

    setChartParams(prev => {
        const start = newSummaryStartDate;
        const end = newSummaryEndDate;
        return {
            ...prev,
            startDate: start,
            endDate: end,
            selectedBin: getInterval(getMinutes(start, end))
        };
    });
}, [newSummaryStartDate, newSummaryEndDate]);

 
    useEffect(() => {
    if (_.isEmpty(userTerminals)) {
        setSelectedUserTerminals([]);
        return;
    }

     const kitSerialNumber = queryParameters?.get('kitSerialNumber');

        if (kitSerialNumber && !_.isEmpty(kitSerialNumber)) {
            const kitsFromDash = kitSerialNumber.split(',').map(item => item.replace(/'/g, ''));
            const userTerminalsMap = new Map(userTerminals.map(terminal => [terminal.kitSerialNumber, terminal]));
            const selectedTerminals = kitsFromDash
                .map(kit => userTerminalsMap.get(kit))
                .filter(Boolean);

            if (selectedTerminals.length > 0) {
                setSelectedUserTerminals(selectedTerminals as UserTerminal[]);
                // setSelectedMetrics([AVAILABLE_STARLINK_METRICS[0]]);
            }
        } else {
        setSelectedUserTerminals([userTerminals[0]]);
        // setSelectedMetrics([AVAILABLE_STARLINK_METRICS[0]]);
        }
    
}, [userTerminals]);

const previousSelectedTerminals = useRef<UserTerminal[]>([]);

useEffect(() => {
    if (_.isEmpty(selectedUserTerminals)) {
         setChartOptions({...defaultChartOptions, });
        return
    };

    const removed = _.difference(
        previousSelectedTerminals.current || [],
        selectedUserTerminals
    );

    if (removed.length > 0) {
        removed.forEach(terminal => {
            selectedMetrics.forEach(metric => {
                clearMetricSeries({
                    metric,
                    userTerminalIds: [terminal.userTerminalId]
                });
            });
        });
    }

    previousSelectedTerminals.current = selectedUserTerminals;

    getStarlinkUserTerminalAlerts({
        user_terminal_ids: selectedUserTerminals
            .map(item => item.userTerminalId)
            .join(','),
        start_ts: chartParams.startDate.format(YYYY_MM_DD_HH_MM_SS),
        end_ts: chartParams.endDate.format(YYYY_MM_DD_HH_MM_SS)
    });

     selectedMetrics?.forEach(metric => {
        getStarlinkMetricSeries({
              source:  getAnalyticsSource(chartParams?.selectedBin),
            start_time: chartParams.startDate.format(YYYY_MM_DD_HH_MM_SS),
            end_time: chartParams.endDate.format(YYYY_MM_DD_HH_MM_SS),
            interval: chartParams.selectedBin,
            user_terminal_ids: selectedUserTerminals.map(ut => ut.userTerminalId).join(','),
            metric
        });
    });

}, [selectedUserTerminals, chartParams.startDate, chartParams.endDate, chartParams.selectedBin, selectedMetrics]);

//     if (_.isEmpty(selectedUserTerminals) || _.isEmpty(selectedMetrics)) return;

//     selectedMetrics.forEach(metric => {
//         getStarlinkMetricSeries({
//             source: "",
//             start_time: chartParams.startDate.format(YYYY_MM_DD_HH_MM_SS),
//             end_time: chartParams.endDate.format(YYYY_MM_DD_HH_MM_SS),
//             interval: chartParams.selectedBin,
//             user_terminal_ids: selectedUserTerminals.map(ut => ut.userTerminalId).join(','),
//             metric
//         });
//     });

// }, [chartParams]);


    const getSeriesData = (series: Record<string, any[]>, valueUnit: string, yAxisCount: any = 0, metric: any = null, useGlobalColorCount: boolean = false, tooltip: any = {}) => {
        return (() => {
           
            return Object.keys(series).map((key: string, i: any) => {
                  const terminal = userTerminals?.find(ut => ut.userTerminalId === key);
                return {
                    ...defaultChartOptions.series,
                    marker: {
                        symbol: 'circle',
                    },
                    name: `${metric || '-'} - ${terminal?.serviceLineName || '-'} - ${terminal?.userTerminalId || '-'}`,
                    data: series[key].map(point => ({
                        ...point,
                        y: parseFloat(point.y.toFixed(2))
                    })),
                    color: useGlobalColorCount ? CHART_COLORS[globalColorCount++] : CHART_COLORS[i],
                    tooltip: _.isEmpty(tooltip) ? {
                        valueSuffix: valueUnit
                    } : tooltip,
                    yAxis: yAxisCount,
                    valueUnit: valueUnit 
                };
            });
        })()
    }

    useEffect(() => {
        const updatedChartOptions = {
            ...defaultChartOptions,
            time: { timezone: getTimezoneCity(authReducer?.userTimezone) },
            chart: {
                ...defaultChartOptions.chart,
                events: {},
                height: "450px"
            },
            title: {
                ...defaultChartOptions.title,
                text: "",
                floating: false
            },
            xAxis: {
                ...defaultChartOptions.xAxis,
                min: moment(newSummaryStartDate).utc().toDate().valueOf(),
                max: moment(newSummaryEndDate).utc().toDate().valueOf(),
                plotLines: (!_.isEmpty(userTerminalAlerts)) ? userTerminalAlerts?.map(row => {
                    const [ts, device_id, alert] = row;
                    return {
                        value: moment(ts).valueOf(),
                        width: 0.5,
                        color: 'red'
                    }
                }) : []
            },
            series: (!_.isEmpty(selectedMetrics) && !_.isEmpty(selectedUserTerminals)) ? (() => {
                let yAxisCount = 0;
                globalColorCount = 0;
                let performanceMetricSeries: any[] = [];
                let eventSeries: any[] = [];
                if (!_.isEmpty(selectedMetrics) && !_.isEmpty(selectedUserTerminals)) {
                    performanceMetricSeries = selectedMetrics.map((metric, i) => {
                        switch (metric) {
                            case "downlink_throughput":
                                return !_.isEmpty(series?.downlink_throughput) ? getSeriesData(series?.downlink_throughput, STARLINK_METRIC_UNITS[metric], yAxisCount++, STARLINK_METRIC_DISPLAY_NAMES[metric], true) : []
                            case "uplink_throughput":
                                return !_.isEmpty(series?.uplink_throughput) ? getSeriesData(series?.uplink_throughput, STARLINK_METRIC_UNITS[metric], yAxisCount++, STARLINK_METRIC_DISPLAY_NAMES[metric], true) : []
                            case "ping_drop_rate":
                                return !_.isEmpty(series?.ping_drop_rate) ? getSeriesData(series?.ping_drop_rate, STARLINK_METRIC_UNITS[metric], yAxisCount++, STARLINK_METRIC_DISPLAY_NAMES[metric], true) : []
                            case "ping_latency_ms":
                                return !_.isEmpty(series?.ping_latency_ms) ? getSeriesData(series?.ping_latency_ms, STARLINK_METRIC_UNITS[metric], yAxisCount++, STARLINK_METRIC_DISPLAY_NAMES[metric], true) : []
                            case "obstruction_percent_time":
                                return !_.isEmpty(series?.obstruction_percent_time) ? getSeriesData(series?.obstruction_percent_time, STARLINK_METRIC_UNITS[metric], yAxisCount++, STARLINK_METRIC_DISPLAY_NAMES[metric], true) : []
                            case "signal_quality":
                                return !_.isEmpty(series?.signal_quality) ? getSeriesData(Object.fromEntries(Object.entries(series.signal_quality).map(([k, arr]: any) => [k, arr.map(p => ({ ...p, y: p.y * 100 }))])), STARLINK_METRIC_UNITS[metric], yAxisCount++, STARLINK_METRIC_DISPLAY_NAMES[metric], true) : []
                        }
                    }).flat();

                    if (!_.isEmpty(userTerminalAlerts) && userTerminalAlerts.length > 0) {
                        const grouped = _.groupBy(userTerminalAlerts, ([ts]) =>
                            moment(ts, 'YYYY-MM-DD HH:mm:ss Z').valueOf()
                        )

                        const seriesData = Object.entries(grouped).map(([timestamp, items]) => {
                            const description = items.map(([, device_id, alert]) =>
                                `<b>Device:</b> ${device_id}<br/><b>Alert:</b> ${alertMap[+alert]}`
                            ).join('<br/><br/>')

                            return {
                                x: +timestamp,
                                y: 0,
                                color: 'red',
                                description
                            }
                        })


                        eventSeries = [{
                              ...defaultChartOptions.series,
                              name: 'Event',
                               color: 'red',  
                              data: seriesData,
                              marker: {
                                  ...defaultChartOptions.series.marker,
                                  enabled: true,
                                  symbol: 'square'
                              },
                              tooltip: {
                                  pointFormat: '{point.description}'
                              },
                            //   yAxis: yAxisCount++
                          }]

                    }
                }
                return cloneDeep([...performanceMetricSeries, ...eventSeries]);
            })() : [],
            yAxis: buildYAxes()
        };

        setChartOptions(updatedChartOptions);
    }, [series, userTerminalAlerts, chartParams?.startDate, chartParams?.endDate, chartParams?.selectedBin]);

    const handleChangeSelectedUserTerminals = (
        event: SyntheticEvent<Element, Event>,
        value: unknown,
        reason: AutocompleteChangeReason) => {
        event.preventDefault();
        setTerminalChange(true)
        if (reason === "selectOption" || reason === "removeOption") {
            setSelectedUserTerminals(value as UserTerminal[]);
        } else if (reason === "clear") {
            setSelectedUserTerminals([]);
        }
    };

    const handleChangeMetrics = (event: SyntheticEvent, value: unknown) => {
        setSelectedMetrics(value as StarlinkMetric[]);
    };

    const handleBinChange = (e) => {
        setChartParams(prev => ({
            ...prev,
            selectedBin: e.target.value
        }));
    };

    const handleDownloadCSV = () => {
        const chart = chartRef.current?.chart;
        if (!chart) return;

        let csvData = chart.getCSV();
        if (!csvData) return;

        const normalized = csvData.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        const lines = normalized.split('\n');

        const headerIndex = lines.findIndex(line => line.toLowerCase().includes('event'));
        if (headerIndex === -1) {
            const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'starlink_insights_data.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return;
        }

        const headerLine = lines[headerIndex];
        const delimiter = headerLine.includes(',') ? ',' : headerLine.includes(';') ? ';' : '\t';
        const rows = lines.map(line => line.split(delimiter));

        let eventColIndex = rows[headerIndex].findIndex(col => col.trim().replace(/^"|"$/g, '').toLowerCase() === 'event');
        if (eventColIndex === -1) {
            eventColIndex = rows[headerIndex].findIndex(col => col.toLowerCase().includes('event'));
        }

        if (eventColIndex === -1) {
            const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'starlink_insights_data.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return;
        }

        const cleanedLines = rows.map(cols => {
            if (cols.length > eventColIndex) cols.splice(eventColIndex, 1);
            return cols.join(delimiter);
        });

        const cleanedCsv = cleanedLines.join('\n');

        const blob = new Blob([cleanedCsv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'starlink_insights_data.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const buildYAxes = () => {
        if (_.isEmpty(selectedMetrics)) return [];

        return selectedMetrics.map((metric, index) => ({
            title: { text: `${STARLINK_METRIC_DISPLAY_NAMES[metric]} (${STARLINK_METRIC_UNITS[metric]})` },
            lineWidth: 1,
            opposite: index !== 0
        }));
    };

    const allowedBins = getAllowedBins(getMinutes(chartParams.startDate, chartParams.endDate));

    return (
        <Grid classes={{ root: 'starlink--base--mtb_07x' }}>
            <Grid container className="alignStarlinkInsightsTopbar">
                <Grid item xs={8} classes={{ root: 'starlink--base--mtb_07x starlink--base--flex--gap--05x starlink--base--flex--row' }}>
                    <Autocomplete
                        id="user_terminals"
                        multiple
                        disableCloseOnSelect
                        fullWidth
                        loading={gettingUserTerminals}
                        options={userTerminals}
                        value={selectedUserTerminals}
                        onChange={handleChangeSelectedUserTerminals}
                        size="small"
                        getOptionLabel={(option: UserTerminal) => `${option.serviceLineName} (${option.kitSerialNumber})`}
                        isOptionEqualToValue={(option, value) => option.kitSerialNumber === value.kitSerialNumber}
                        renderInput={(params) => <TextField {...params} label="Starlinks" variant="outlined" />}
                        sx={{ maxWidth: 400, "& .MuiAutocomplete-inputRoot": { flexWrap: 'unset' } }}
                        renderTags={(value: UserTerminal[], getTagProps) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 58, overflowY: 'auto' }}>
                                {value.map((option, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        size="small"
                                        label={`${option.serviceLineName} (${option.kitSerialNumber})`}
                                    />
                                ))}
                            </Box>
                        )}
                    />
                    <Autocomplete
                        id="metrics"
                        multiple
                        disableCloseOnSelect
                        fullWidth
                        loading={false}
                        options={AVAILABLE_STARLINK_METRICS}
                        value={selectedMetrics}
                        onChange={handleChangeMetrics}
                        size="small"
                        getOptionLabel={(option) => STARLINK_METRIC_DISPLAY_NAMES[option]}
                        renderInput={(params) => <TextField {...params} label="Metrics" variant="outlined" />}
                        sx={{ maxWidth: 450, "& .MuiAutocomplete-inputRoot": { flexWrap: 'unset' } }}
                        renderTags={(value: string[], getTagProps) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 58, overflowY: 'auto' }}>
                                {value.map((option, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        size="small"
                                        label={STARLINK_METRIC_DISPLAY_NAMES[option]}
                                    />
                                ))}
                            </Box>
                        )}
                    />
                </Grid>
                <Grid item xs={4} classes={{ root: 'starlink--base--flex--row starlink--base--flex--justify--end starlink--base--flex--align--center starlink--base--flex--gap--05x starlinkInsightsMaxwidth' }}>
                    <FormControl variant="standard" className='selectEntry-pie sdwan-select-bin'>
                        <Select
                            labelId="bin-select-label"
                            id="bin-select"
                            value={chartParams.selectedBin}
                            onChange={handleBinChange}
                        >
                            {BINS?.filter(b => allowedBins?.includes(b?.value))?.map(b => (
                                <MenuItem key={b?.value} value={b?.value}>
                                    {b?.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                </Grid>
                <div className="download-summary-btn margin-left-auto downloadHeight" onClick={handleDownloadCSV}>
                    <img className="downloadImg" src={Download} alt="" />
                    <Button>Download</Button>
                </div>
            </Grid>

            <Grid classes={{ root: 'starlink--base--mtb_07x', }}>

                <Chart
                    chartOptions={chartOptions}
                    immutable={true}
                    updateArgs={[true, false, true]}
                    parameters={{}}
                    page={0}
                    setPage={0}
                    enableEventsTable={false}
                    uptimeChart={''}
                    chartRef={chartRef}
                />
            </Grid>

            {userTerminalAlerts?.length > 0 && selectedMetrics?.length > 0 && (
                <TableContainer className="alertsTable-FleetDashboard">
                    <Table aria-label="alerts table">
                        <TableHead className="tableHead alertsTable-tableHead">
                            <TableRow className="alertReports-tableRow">
                                <TableCell className="alertsTable-fleetDataTable font-wt-900">USER TERMINAL</TableCell>
                                <TableCell className="alertsTable-fleetDataTable font-wt-900">ALERT</TableCell>
                                <TableCell className="alertsTable-fleetDataTable font-wt-900">TIMESTAMP</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className="tableBody alertsTable-tableBody">
                            {userTerminalAlerts?.map((row, index) => {
                                const [ts, device_id, alert] = row;
                                return (
                                    <TableRow key={index} className="accounts-tabelRow">
                                        <TableCell className="accounts-tableCell noWrapAccounts">{device_id}</TableCell>
                                        <TableCell className="accounts-tableCell noWrapAccounts">{alertMap[alert]}</TableCell>
                                        <TableCell className="accounts-tableCell noWrapAccounts">
                                            {moment(ts)?.utc()?.format(YYYY_MM_DD_HH_MM_SS)}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Grid>
    );
}

const mapStateToProps = (state) => ({
    authReducer: state.authReducer,
    errorReducer: state.errorReducer,
    dpIds: state.starlinkCloud.dpIds,
    userTerminals: state.starlinkAnalytics.userTerminals,
    gettingUserTerminals: state.starlinkAnalytics.gettingUserTerminals,
    series: state.starlinkAnalytics.series,
    userTerminalAlerts: state.starlinkAnalytics.userTerminalAlerts,
    userTerminalAlertColumns: state.starlinkAnalytics.userTerminalAlertColumns,
    gettingMetricSeries: state.starlinkAnalytics.gettingMetricSeries,
    newSummaryStartDate: state?.authReducer?.newSummaryStartDate,
    newSummaryEndDate: state?.authReducer?.newSummaryEndDate,
    newSummaryServiceLine: state?.authReducer?.newSummaryServiceLine
});

export default withRouter(
    connect(mapStateToProps, {
        getDeployedUserTerminalsByDP,
        getStarlinkMetricSeries,
        getStarlinkUserTerminalAlerts,
        clearMetricSeries,
        downloadAsCSV,
        clearComponentData
    })(StarlinkAnalytics)
);